import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin } from "lucide-react";
import SearchResultItem from "./SearchResultItem";
import { generatePath, useLocation, useNavigate } from "react-router-dom";
import { Paths } from "@/src/constants/Paths";
import { useDispatch, useSelector } from "react-redux";
import { searchTourPackages } from "@/src/actions/tour/tourAction";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";

import { slugify } from "@/src/helpers/commonHelper";

import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Filter } from "lucide-react";
import { TourFilterSidebar } from "./TourFilterSidebar";

function SearchResult({ result, initialQuery, month, pageSize: pageSizeProp }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loaderRef = useRef(null);
  const { search, hash } = useLocation();

  // ✅ API shape: { Items, RowTotal, PageNumber, PageSize }
  const initialItems = result?.Items ?? [];
  const initialRowTotal = result?.RowTotal ?? 0;
  const initialPageNumber = result?.PageNumber ?? 1;
  const initialPageSize = result?.PageSize ?? Number(pageSizeProp ?? 10);

  const [items, setItems] = useState(initialItems);
  const [rowTotal, setRowTotal] = useState(initialRowTotal);
  const [page, setPage] = useState(initialPageNumber);
  const [pageSize] = useState(Number(initialPageSize));
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  //this will filter the tour packages
  const [filters, setFilters] = useState({
    priceRange: null,
    durationRange: null,
    groupSizeRange: null,
    activities: [],
    departureTypes: [],
    departureMonths: [],
  });

  const { minMaxValues, availableActivities, availableMonths } = useMemo(() => {
    let minPrice = Infinity;
    let maxPrice = 0;
    let minDuration = Infinity;
    let maxDuration = 0;
    let minGroup = Infinity;
    let maxGroup = 0;
    const activitiesSet = new Set();
    const monthsSet = new Set();

    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];

    items.forEach((item) => {
      // Price & Departure Months from the pricing of the package
      if (item.Prices?.length > 0) {
        const prices = item.Prices.map(p => (Number(p.TourFare) || 0) + (Number(p.FlightFare) || 0)).filter(p => !isNaN(p) && p > 0);
        if (prices.length > 0) {
          minPrice = Math.min(minPrice, ...prices);
          maxPrice = Math.max(maxPrice, ...prices);
        }

        item.Prices.forEach(p => {
          const dateVal = p.TripStartDate || p.DepartureDate;
          if (dateVal) {
            const date = new Date(dateVal);
            if (!isNaN(date.getTime())) {
              monthsSet.add(monthNames[date.getMonth()]);
            }
          }
        });
      }

      // Duration from the attributes of the package
      const daysAttr = item.Attributes?.find(a => a.AttrKey === "Days");
      const days = daysAttr ? parseInt(daysAttr.AttrValue, 10) : 0;
      if (days > 0) {
        minDuration = Math.min(minDuration, days);
        maxDuration = Math.max(maxDuration, days);
      }

      // Group Size from the attributes of the package
      const groupAttr = item.Attributes?.find(a => a.AttrKey === "Group Size");
      const size = groupAttr ? parseInt(groupAttr.AttrValue, 10) : 0;
      if (size > 0) {
        minGroup = Math.min(minGroup, size);
        maxGroup = Math.max(maxGroup, size);
      }

      // Activities from the itineraries of the package, will probably be used in the future
      if (item.Itineraries) {
        item.Itineraries.forEach(itinerary => {
          if (itinerary.ActivityInfo) {
            const parts = itinerary.ActivityInfo.split(',');
            parts.forEach(p => {
              const clean = p.trim();
              if (clean.length > 2 && clean.length < 50) {
                activitiesSet.add(clean);
              }
            });
          }
        })
      }
    });

    if (minPrice === Infinity) minPrice = 0;
    if (maxPrice === 0) maxPrice = 10000;
    if (minDuration === Infinity) minDuration = 1;
    if (maxDuration === 0) maxDuration = 30;
    if (minGroup === Infinity) minGroup = 1;
    if (maxGroup === 0) maxGroup = 50;

    return {
      minMaxValues: {
        price: [minPrice, maxPrice],
        duration: [minDuration, maxDuration],
        groupSize: [minGroup, maxGroup]
      },
      availableActivities: Array.from(activitiesSet).sort(),
      availableMonths: Array.from(monthsSet).sort((a, b) => monthNames.indexOf(a) - monthNames.indexOf(b))
    };
  }, [items]);

  const handleFilterChange = (key, value) => {
    if (key === "reset") {
      setFilters({
        priceRange: null,
        durationRange: null,
        groupSizeRange: null,
        activities: [],
        departureTypes: [],
        departureMonths: [],
      });
    } else {
      setFilters((prev) => ({ ...prev, [key]: value }));
    }
  };

  // ✅ When parent result changes (new search), reset state
  useEffect(() => {
    setItems(result?.Items ?? []);
    setRowTotal(result?.RowTotal ?? 0);
    setPage(result?.PageNumber ?? 1);
  }, [result]);

  const { destinationOptions } = useSelector((state) => state.tours);
  const query = destinationOptions?.find((d) => d.Value === initialQuery)?.Text || "";

  const filteredItems = useMemo(() => {
    let filtered = items;

    // filtering by Price Range
    if (filters.priceRange) {
      filtered = filtered.filter((item) => {
        if (!item.Prices || item.Prices.length === 0) return false;
        // Check if ANY price option falls within range
        const itemPrices = item.Prices.map(p => (Number(p.TourFare) || 0) + (Number(p.FlightFare) || 0));
        return itemPrices.some(p => p >= filters.priceRange[0] && p <= filters.priceRange[1]);
      });
    }

    // filtering by Duration (Days)
    if (filters.durationRange) {
      filtered = filtered.filter((item) => {
        const daysAttr = item.Attributes?.find(a => a.AttrKey === "Days");
        const days = daysAttr ? parseInt(daysAttr.AttrValue, 10) : 0;
        return days >= filters.durationRange[0] && days <= filters.durationRange[1];
      });
    }

    // filtering by Group Size
    if (filters.groupSizeRange) {
      filtered = filtered.filter((item) => {
        const groupAttr = item.Attributes?.find(a => a.AttrKey === "Group Size");
        const size = groupAttr ? parseInt(groupAttr.AttrValue, 10) : 0;
        return size >= filters.groupSizeRange[0] && size <= filters.groupSizeRange[1];
      });
    }

    // filtering by Departure Type, IsGroup
    if (filters.departureTypes?.length > 0) {
      filtered = filtered.filter((item) => {
        if (!item.Prices || item.Prices.length === 0) return false;

        const hasCustom = item.Prices.some(p => p.IsGroup === true);
        const hasFixed = item.Prices.some(p => p.IsGroup === false);

        if (filters.departureTypes.includes('custom') && filters.departureTypes.includes('fixed')) {
          return hasCustom || hasFixed;
        }
        if (filters.departureTypes.includes('custom')) {
          return hasCustom;
        }
        if (filters.departureTypes.includes('fixed')) {
          return hasFixed;
        }
        return true;
      });
    }

    // filtering by Departure Months
    if (filters.departureMonths?.length > 0) {
      const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
      ];
      filtered = filtered.filter((item) => {
        if (!item.Prices || item.Prices.length === 0) return false;
        return item.Prices.some(p => {
          const dateVal = p.TripStartDate || p.DepartureDate;
          if (dateVal) {
            const date = new Date(dateVal);
            if (!isNaN(date.getTime())) {
              return filters.departureMonths.includes(monthNames[date.getMonth()]);
            }
          }
          return false;
        });
      });
    }

    // filtering by Activities
    if (filters.activities?.length > 0) {
      filtered = filtered.filter(item => {

        const itemActivities = new Set();
        if (item.Itineraries) {
          item.Itineraries.forEach(itinerary => {
            if (itinerary.ActivityInfo) {
              const parts = itinerary.ActivityInfo.split(',');
              parts.forEach(p => itemActivities.add(p.trim()));
            }
          });
        }
        return filters.activities.some(act => itemActivities.has(act));
      });
    }

    return filtered;
  }, [items, filters]);


  const hasResults = filteredItems.length > 0;
  // Use filteredItems length for display if filtering is active, but pagination logic relies on server fetch.
  // Caveat: Infinite scroll fetches more items, which are then filtered.
  // This might look weird if all fetched items are filtered out (user sees "Loading..." then nothing new appears).
  // But for client-side filtering on top of server pagination, this is the standard pattern without backend filter support.

  const totalPages = useMemo(() => {
    if (!rowTotal || !pageSize) return 1;
    return Math.max(1, Math.ceil(rowTotal / pageSize));
  }, [rowTotal, pageSize]);

  const hasMore = page < totalPages;

  const handleOpenPackage = (item) => {
    const packageId = item?.Package?.PackageID;
    const title = item?.Package?.Title || "package";
    if (!packageId) return;
    navigate({
      pathname: generatePath(Paths.TOUR_PACKAGE_DETAIL, {
        packageId: packageId,
        packageName: slugify(title)
      }),
      search: search // This preserves the existing search string
    });
  };

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    const nextPage = page + 1;

    // ✅ Use same action as parent (so same backend + headers + auth)
    dispatch(
      searchTourPackages(
        initialQuery, // destinationId or destination? (you are passing destination in parent)
        pageSize,
        nextPage,
        month ?? "",
        (resp) => {
          const next = resp?.Result; // { Items, RowTotal, PageNumber, PageSize }
          const nextItems = next?.Items ?? [];

          if (nextItems.length > 0) {
            setItems((prev) => [...prev, ...nextItems]);
          }

          setRowTotal(next?.RowTotal ?? rowTotal);
          setPage(next?.PageNumber ?? nextPage);
          setIsLoadingMore(false);
        }
      )
    );
  }, [dispatch, hasMore, isLoadingMore, page, pageSize, query, month, rowTotal, initialQuery]); // Added initialQuery to dep

  // 👀 IntersectionObserver for infinite scroll
  useEffect(() => {
    const target = loaderRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      {
        root: null,
        rootMargin: "200px",
        threshold: 0,
      }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [loadMore]);

  useEffect(() => {
    if (hash) {
      // Remove the '#' from the string to get the ID
      const id = hash.replace('#', '');
      const element = document.getElementById(id);

      if (element) {
        // Smoothly scroll to the item
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [hash, items]); // Re-run if hash changes or list data loads

  return (
    <div className="w-full min-h-screen text-foreground bg-background">
      {/* Header */}
      <section className="w-full border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col gap-2">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                Search results
              </p>

              <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
                {query ? (
                  <>
                    Trips for <span className="text-primary">“{query}”</span>
                    {month && month !== "0" && (
                      <> on <span className="text-primary">{format(new Date(month + "-01"), "MMMM yyyy")}</span></>
                    )}
                  </>
                ) : (
                  "Available tour packages"
                )}
              </h1>
            </div>

            {/* Mobile Filter Trigger */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="lg:hidden gap-2">
                  <Filter className="h-4 w-4" />
                  Filters
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[300px] sm:w-[350px]">
                <div className="py-4">
                  <TourFilterSidebar
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    minMaxValues={minMaxValues}
                    availableActivities={availableActivities}
                    availableMonths={availableMonths}
                  />
                </div>
              </SheetContent>
            </Sheet>
          </div>

          <p className="text-sm text-muted-foreground max-w-2xl">
            Explore curated trips and holiday packages. Compare itineraries,
            inclusions, and prices before you book on{" "}
            <span className="font-semibold text-primary">yatratickets.com</span>.
          </p>

          <p className="text-xs text-muted-foreground">
            Showing {filteredItems.length} of {rowTotal} packages
          </p>
        </div>
      </section>

      {/* Results & Sidebar */}
      <section className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Desktop */}
          <aside className="hidden lg:block w-72 flex-shrink-0">
            <div className="sticky top-24">
              <TourFilterSidebar
                filters={filters}
                onFilterChange={handleFilterChange}
                minMaxValues={minMaxValues}
                availableActivities={availableActivities}
                availableMonths={availableMonths}
              />
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Empty State */}
            {!hasResults && !isLoadingMore && (
              <Card className="border border-dashed">
                <CardContent className="py-10 flex flex-col items-center justify-center text-center gap-3">
                  <div className="h-12 w-12 flex items-center justify-center border border-border">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>

                  <h2 className="font-semibold text-base">
                    No trips found for this search
                  </h2>

                  <p className="text-sm text-muted-foreground max-w-md">
                    Try adjusting your filters, searching for another destination, or broadening your dates.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Results List */}
            {hasResults && (
              <div className="space-y-3">
                {filteredItems.map((item) => (
                  <SearchResultItem
                    key={item?.Package?.PackageID}
                    item={item}
                    onOpen={() => handleOpenPackage(item)}
                  />
                ))}

                {/* Infinite scroll sentinel */}
                <div ref={loaderRef} className="h-10 flex items-center justify-center">
                  {isLoadingMore && (
                    <span className="text-xs text-muted-foreground">
                      Loading more trips...
                    </span>
                  )}
                  {!hasMore && !isLoadingMore && (
                    <span className="text-[11px] text-muted-foreground">
                      You have reached the end of the list.
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default SearchResult;
